import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Download, Heart, Sparkles, CheckCircle } from 'lucide-react';
import { freeResources } from '../data/freeResources';
import { toast } from 'sonner';

export const FiveLiesGuide = () => {
  const { fiveLies } = freeResources;

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  const handleDownloadPDF = () => {
    // Create printable HTML and trigger print/save as PDF
    const printContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>${fiveLies.title} - Breakthrough</title>
        <style>
          @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,400;0,600;1,400&family=Nunito:wght@400;600&display=swap');
          
          * { box-sizing: border-box; margin: 0; padding: 0; }
          
          body {
            font-family: 'Nunito', sans-serif;
            color: #2D2030;
            line-height: 1.8;
            padding: 50px;
            max-width: 700px;
            margin: 0 auto;
          }
          
          h1, h2, h3 { font-family: 'Cormorant Garamond', serif; }
          
          .header {
            text-align: center;
            margin-bottom: 40px;
            padding-bottom: 30px;
            border-bottom: 2px solid #7B1045;
          }
          
          .header h1 { font-size: 36px; color: #2D2030; margin-bottom: 8px; }
          .header p { font-style: italic; color: #6B5B6E; font-size: 18px; }
          
          .intro {
            background: #FBF5F7;
            padding: 25px;
            border-radius: 8px;
            margin-bottom: 40px;
          }
          
          .intro p { margin-bottom: 12px; }
          
          .lie-section {
            margin-bottom: 40px;
            page-break-inside: avoid;
          }
          
          .lie-header {
            background: linear-gradient(135deg, #E57373 0%, #EF9A9A 100%);
            color: white;
            padding: 15px 20px;
            border-radius: 8px 8px 0 0;
          }
          
          .lie-header h3 { font-size: 14px; text-transform: uppercase; letter-spacing: 1px; margin-bottom: 5px; }
          .lie-header p { font-size: 20px; font-style: italic; }
          
          .truth-section {
            background: linear-gradient(135deg, #7B1045 0%, #A8366B 100%);
            color: white;
            padding: 15px 20px;
          }
          
          .truth-section h3 { font-size: 14px; text-transform: uppercase; letter-spacing: 1px; margin-bottom: 5px; }
          .truth-section p { font-size: 18px; }
          
          .scripture {
            background: #FFF9E6;
            padding: 20px;
            border-left: 4px solid #D4AF37;
            margin: 0;
          }
          
          .scripture p { font-family: 'Cormorant Garamond', serif; font-style: italic; font-size: 18px; }
          .scripture span { display: block; color: #D4AF37; font-size: 14px; margin-top: 8px; font-style: normal; }
          
          .reflection {
            padding: 20px;
            background: #FEFCFD;
            border-radius: 0 0 8px 8px;
            border: 1px solid #E5E5E5;
            border-top: none;
          }
          
          .closing {
            text-align: center;
            margin-top: 50px;
            padding: 30px;
            background: linear-gradient(135deg, #D4A0B533, #7B104522);
            border-radius: 8px;
          }
          
          .closing p { margin-bottom: 12px; }
          
          .footer {
            text-align: center;
            margin-top: 40px;
            padding-top: 20px;
            border-top: 1px solid #E5E5E5;
            color: #6B5B6E;
          }
          
          @media print {
            body { padding: 20px; }
          }
        </style>
      </head>
      <body>
        <div class="header">
          <h1>${fiveLies.title}</h1>
          <p>${fiveLies.subtitle}</p>
          <p style="font-size: 14px; color: #7B1045; margin-top: 15px;">By Janelle McGowan-Charles</p>
          <p style="font-size: 12px; color: #6B5B6E;">Purpose Reclaimed Coaching</p>
        </div>
        
        <div class="intro">
          ${fiveLies.introduction.split('\n\n').map(p => `<p>${p}</p>`).join('')}
        </div>
        
        ${fiveLies.lies.map((item, idx) => `
          <div class="lie-section">
            <div class="lie-header">
              <h3>Lie #${idx + 1}</h3>
              <p>"${item.lie}"</p>
            </div>
            <div class="truth-section">
              <h3>The Truth</h3>
              <p>${item.truth}</p>
            </div>
            <div class="scripture">
              <p>"${item.scripture}"</p>
              <span>— ${item.reference}</span>
            </div>
            <div class="reflection">
              <p>${item.reflection}</p>
            </div>
          </div>
        `).join('')}
        
        <div class="closing">
          ${fiveLies.closing.split('\n\n').map(p => `<p>${p}</p>`).join('')}
        </div>
        
        <div class="footer">
          <p style="font-weight: bold;">By Janelle McGowan-Charles</p>
          <p style="color: #7B1045;">Purpose Reclaimed Coaching</p>
          <p style="margin-top: 15px;">© ${new Date().getFullYear()} Janelle McGowan-Charles, Purpose Reclaimed Coaching. All rights reserved.</p>
        </div>
      </body>
      </html>
    `;

    const printWindow = window.open('', '_blank');
    printWindow.document.write(printContent);
    printWindow.document.close();
    printWindow.focus();
    
    setTimeout(() => {
      printWindow.print();
    }, 500);

    toast.success('PDF ready to save!', {
      description: 'Choose "Save as PDF" in the print dialog'
    });
  };

  return (
    <div data-testid="five-lies-guide" className="min-h-screen bg-cream pb-16">
      {/* Header */}
      <div className="sticky top-0 bg-white/95 backdrop-blur-md border-b border-stone-100 z-50">
        <div className="max-w-3xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link 
            to="/free"
            data-testid="back-to-freebies-5lies"
            className="flex items-center gap-2 text-charcoal-light hover:text-sage transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
            <span className="hidden sm:inline">Back</span>
          </Link>
          <span className="text-sm font-medium text-gold">FREE GUIDE</span>
          <button
            onClick={handleDownloadPDF}
            data-testid="download-5lies-pdf"
            className="flex items-center gap-2 text-sage hover:text-sage-dark transition-colors"
          >
            <Download className="w-5 h-5" />
            <span className="hidden sm:inline">Save PDF</span>
          </button>
        </div>
      </div>

      {/* Hero */}
      <div className="bg-gradient-to-br from-gold-light/40 to-cream py-12 md:py-16">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <Heart className="w-12 h-12 text-gold mx-auto mb-4" />
          <h1 className="font-heading text-4xl md:text-5xl text-charcoal mb-3 animate-fade-in">
            {fiveLies.title}
          </h1>
          <p className="font-heading text-xl text-charcoal-light italic">
            {fiveLies.subtitle}
          </p>
        </div>
      </div>

      {/* Introduction */}
      <div className="max-w-3xl mx-auto px-6 py-10">
        <div className="bg-white rounded-xl p-8 shadow-sm">
          {fiveLies.introduction.split('\n\n').map((paragraph, idx) => (
            <p key={idx} className="text-charcoal/90 leading-relaxed mb-4 last:mb-0">
              {paragraph}
            </p>
          ))}
        </div>
      </div>

      {/* The 5 Lies */}
      <div className="max-w-3xl mx-auto px-6 space-y-8">
        {fiveLies.lies.map((item, index) => (
          <div 
            key={index}
            data-testid={`lie-${index + 1}`}
            className="rounded-2xl overflow-hidden shadow-lg animate-slide-up"
            style={{ animationDelay: `${index * 100}ms` }}
          >
            {/* The Lie */}
            <div className="bg-gradient-to-r from-red-300 to-red-200 p-6">
              <span className="text-xs font-bold text-red-800 uppercase tracking-wider">
                Lie #{index + 1}
              </span>
              <p className="font-heading text-2xl text-red-900 italic mt-2">
                "{item.lie}"
              </p>
            </div>

            {/* The Truth */}
            <div className="bg-gradient-to-r from-sage to-sage-light p-6">
              <span className="text-xs font-bold text-white/80 uppercase tracking-wider">
                The Truth
              </span>
              <p className="font-body text-lg text-white mt-2">
                {item.truth}
              </p>
            </div>

            {/* Scripture */}
            <div className="bg-gold-light/30 p-6 border-l-4 border-gold">
              <p className="font-heading text-xl italic text-charcoal">
                "{item.scripture}"
              </p>
              <p className="text-gold font-medium text-sm mt-2">
                — {item.reference}
              </p>
            </div>

            {/* Reflection */}
            <div className="bg-white p-6">
              <p className="text-charcoal/80 leading-relaxed">
                {item.reflection}
              </p>
            </div>
          </div>
        ))}
      </div>

      {/* Closing */}
      <div className="max-w-3xl mx-auto px-6 py-12">
        <div className="bg-gradient-to-br from-sky-light/50 to-sage-light/30 rounded-2xl p-8 md:p-10 text-center">
          <Sparkles className="w-8 h-8 text-gold mx-auto mb-4" />
          {fiveLies.closing.split('\n\n').map((paragraph, idx) => (
            <p key={idx} className="text-charcoal/90 leading-relaxed mb-4 last:mb-0">
              {paragraph}
            </p>
          ))}
        </div>
      </div>

      {/* CTA */}
      <div className="max-w-3xl mx-auto px-6">
        <div className="bg-white rounded-2xl p-8 shadow-lg text-center">
          <h3 className="font-heading text-2xl text-charcoal mb-3">
            Ready to Go Deeper?
          </h3>
          <p className="text-charcoal-light mb-6 max-w-lg mx-auto">
            {fiveLies.cta}
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link
              to="/contents"
              data-testid="get-full-guide-from-5lies"
              className="inline-flex items-center gap-2 px-8 py-4 bg-sage text-white rounded-full font-medium hover:bg-sage-dark transition-all shadow-lg"
            >
              Get the Full Breakthrough Guide
              <ArrowRight className="w-5 h-5" />
            </Link>
            <Link
              to="/free/chapter-1"
              className="text-sage hover:text-sage-dark font-medium"
            >
              Or read Chapter 1 free →
            </Link>
          </div>
        </div>

        {/* Copyright Footer */}
        <div className="max-w-3xl mx-auto px-6 mt-12 pt-6 border-t border-stone-200 text-center">
          <p className="text-sm text-charcoal-light">By Janelle McGowan-Charles</p>
          <p className="text-xs text-charcoal-light/70 mt-1">Purpose Reclaimed Coaching</p>
          <p className="text-xs text-charcoal-light/60 mt-2">© {new Date().getFullYear()} Janelle McGowan-Charles, Purpose Reclaimed Coaching. All rights reserved.</p>
        </div>
      </div>
    </div>
  );
};
